import React, {
    useContext,
    createContext,
    useCallback,
    useMemo,
    useState,
} from 'react'
import { CircularProgress, Modal } from '@mui/material'
import { Box } from '@mui/system'

export interface LoadingInfo {
    show(): void
    hide(): void
    logicShow(): void
    logicHide(): void
    showDocument(): void
    hideDocument(): void
    isLoading: boolean
    isLoadingFullScreen: boolean
    isLoadingDocument: boolean
}

const loadingDefault: LoadingInfo = {
    show: () => {},
    hide: () => {},
    logicShow: () => {},
    logicHide: () => {},
    showDocument: () => {},
    hideDocument: () => {},
    isLoading: false,
    isLoadingFullScreen: false,
    isLoadingDocument: false,
}

const LoadingContext = createContext<LoadingInfo>(loadingDefault)

export function useLoading() {
    return useContext(LoadingContext)
}

interface Props {
    children: React.ReactNode
}

export function LoadingProvider({ children }: Props) {
    const [open, setOpen] = useState(0)
    const [logicOpen, setLogicOpen] = useState(0)
    const [document, setDocument] = useState(false)

    const show = useCallback(() => {
        setOpen((prev) => prev + 1)
    }, [])

    const hide = useCallback(() => {
        setOpen((prev) => prev - 1)
    }, [])

    const logicShow = useCallback(() => {
        setLogicOpen((prev) => prev + 1)
    }, [])

    const logicHide = useCallback(() => {
        setLogicOpen((prev) => prev - 1)
    }, [])

    const showDocument = useCallback(() => {
        setDocument(true)
    }, [])

    const hideDocument = useCallback(() => {
        setDocument(false)
    }, [])

    const value = useMemo(
        () => ({
            show,
            hide,
            logicShow,
            logicHide,
            isLoading: open > 0 || logicOpen > 0,
            isLoadingFullScreen: open > 0,
            showDocument,
            hideDocument,
            isLoadingDocument: document,
        }),
        [
            show,
            hide,
            logicShow,
            logicHide,
            showDocument,
            hideDocument,
            open,
            logicOpen,
            document,
        ]
    )

    return (
        <LoadingContext.Provider value={value}>
            <Modal open={Boolean(open)} className="modal">
                <Box
                    sx={{
                        transform: 'translate(-50px, -100px)',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        outline: 'none',
                    }}
                >
                    <CircularProgress size="100px" />
                </Box>
            </Modal>
            {children}
        </LoadingContext.Provider>
    )
}
